import React, { useRef, useState, useEffect} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EditOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import EmailEditor, { EditorRef } from "react-email-editor";
import EmailStatusPopup from "../Engagements/Campaigns/AIEmailEditor/EmailStatusPopup";
import { sendCampaignEmail} from "src/services/CampaignAPI";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import "../Engagements/Campaigns/AIEmailEditor/AIEmailEditor.scss";

interface LocationState {
  campaignName: string;
  replyTo: string;
  recipients: string;
  subjectLine: string;
  fromName: string;
  fromEmail: string;
  startDate: string | null;
  endDate: string | null;
  popupType?: "scheduled" | "sent" | null;
  emailDesign?: any;
  emailHtml?: string;
  mode?: "manual" | "ai";
  templateType?: "json" | "html";
}

interface SchedulePreset {
  label: string;
  time: string;
  dayOffset?: number;
  kind: "tomorrowMorning" | "tomorrowEvening" | "nextMonday";
}

const ReviewAndSend: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const locationState = state as LocationState;
  const navigate = useNavigate();
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [editingField, setEditingField] = useState<string | null>(null);
const [isSending, setIsSending] = useState(false);
const [templateHTML, setTemplateHTML] = useState<any>(null);
const [templateType, setTemplateType] = useState<LocationState["templateType"] | undefined>(undefined);
  const [campaignName, setCampaignName] = useState(locationState?.campaignName || "");
   const [sendTo, setSendTo] = useState<string[]>(
    locationState?.recipients
      ? locationState.recipients.split(",").map(e => e.trim())
      : []
  );
  const [fromName, setFromName] = useState(
    localStorage.getItem("company_name") || t("campaignFlow.common.defaultCompany"),
  );
  const [fromEmail, setFromEmail] = useState("marketing@datatram.ai");
  const [replyTo, setReplyTo] = useState("support@datatram.com");
  const [subjectLine, setSubjectLine] = useState(
    t("campaignFlow.reviewAndSend.defaultSubjectLine"),
  );

  const emailEditorRef = useRef<EditorRef | null>(null);
  const [popupType, setPopupType] = useState<"scheduled" | "sent" | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [scheduledRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const startScheduled: Dayjs | null = scheduledRange[0];
  const [renderedHTML, setRenderedHTML] = useState<string | null>(null);

useEffect(() => {
  if (!locationState) {return;}

  setTemplateType(locationState.templateType);

  // If JSON exists, keep it for editor
  if (locationState.emailDesign) {
    setTemplateHTML(locationState.emailDesign);
  }

  // If HTML exists, keep it for sending
  if (locationState.emailHtml) {
    setRenderedHTML(locationState.emailHtml); // <-- Add this state
  }

}, [locationState]);

  const renderField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    type: "text" | "email" = "text",
    fieldName: string
  ) => (
    <div className="summary-item" key={label}>
      <label>{label}</label>
      <div className="summary-input-wrapper">
        <div className="input-with-icon-container">
          <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            className="form-input-summary"
            readOnly={fieldName === "fromEmail" || editingField !== fieldName}
          />

          {fieldName !== "fromEmail" &&
            (editingField === fieldName ? (
              <CheckOutlined
                className="edit-icon inside-input"
                onClick={() => setEditingField(null)}
              />
            ) : (
              <EditOutlined
                className="edit-icon inside-input"
                onClick={() => setEditingField(fieldName)}
              />
            ))}
        </div>
      </div>
    </div>
  );

const handleSendCampaign = async (sendAt?: string) => {
  try {
    setIsSending(true);

    const html = await new Promise<string>(resolve => {
      emailEditorRef.current?.editor?.exportHtml((data: any) => {
        resolve(data.html);
      });
    });

    const payload = {
      tenant_id: 0,
      campaign_name: campaignName,
      send_to: sendTo,
      dont_send_to: [],
      html,
      send_at: sendAt || new Date().toISOString(),
      company_name_engagements: fromName,
    };

    const res = await sendCampaignEmail(payload);

if (sendAt) {
  message.success(t("campaignFlow.reviewAndSend.campaignScheduled"));
} else {
  message.success(t("campaignFlow.reviewAndSend.campaignSent"));
}
setPopupType(sendAt ? "scheduled" : "sent");
} catch (err) {
  message.error(t("campaignFlow.reviewAndSend.sendError"));
} finally {
  setIsSending(false);
}
};

  const getPresetDateTime = (preset: SchedulePreset) => {
    const today = new Date();
    const targetDate = new Date(today);

    if (preset.kind === "tomorrowMorning" || preset.kind === "tomorrowEvening") {
      targetDate.setDate(today.getDate() + 1);
    } else if (preset.kind === "nextMonday") {
      const dayOffset = preset.dayOffset || ((8 - today.getDay()) % 7 || 7);
      targetDate.setDate(today.getDate() + dayOffset);
    }

    const dateStr = targetDate.toISOString().split("T")[0];
    return { dateStr, time: preset.time };
  };

  const handleQuickSchedule = (preset: SchedulePreset) => {
    const { dateStr, time } = getPresetDateTime(preset);
    setScheduledDate(dateStr);
    setScheduledTime(time);
  };

const handleEditorReady = () => {
  const editor = emailEditorRef.current?.editor;
  if (!editor) {return;}

  // 1) JSON always takes priority for EDITING
  if (templateType === "json" && typeof templateHTML === "object") {
    editor.loadDesign(templateHTML);
    return;
  }

// 2) Fallback to HTML import if JSON not available
if (typeof renderedHTML === "string") {
  (window as any).unlayer.importHtml(renderedHTML);
}
};

  return (
    <div className="manual-review-step review-send-page">
      {errorMsg && <div className="ai-error-toast">✖ {errorMsg}</div>}

      <div className="campaign-summary-box review-send-shell">
        <div className="page-header">
          <button
            className="back-button back-button--text button button--primary"
            onClick={() => navigate(-1)}
          >
            {t("campaignFlow.common.back")}
          </button>

          <span className="Page-header-text">
            {t("campaignFlow.common.aiReviewBreadcrumb")}
          </span>
        </div>

        <h2 className="form-title review-send-title-row">
          {t("campaignFlow.common.reviewAndSendCampaignSummary")}
        </h2>
        <div className="review-step">
          <div className="Manual-summary-box">
            <div className="summary-grid">
              {renderField(t("campaignFlow.common.campaignName"), campaignName, setCampaignName, "text", "campaignName")}
              {renderField(t("campaignFlow.common.replyTo"), replyTo, setReplyTo, "email", "replyTo")}
              {renderField(t("campaignFlow.common.recipients"),sendTo.join(", "),v => setSendTo(v.split(",").map(e => e.trim())),"text","recipients")}
              {renderField(t("campaignFlow.common.subjectLine"), subjectLine, setSubjectLine, "text", "subjectLine")}
              {renderField(t("campaignFlow.common.fromName"), fromName, setFromName, "text", "fromName")}
              {renderField(t("campaignFlow.common.fromEmail"), fromEmail, setFromEmail, "email", "fromEmail")}
            </div>

           <div className="email-editor-container">
             <EmailEditor ref={emailEditorRef} onLoad={handleEditorReady} options={{ displayMode: "email" }} /> 
             </div>
          </div>

          <div className="Email-footer-actions">
            <button
              className="button button--primary"
              onClick={() => setShowSchedulePopup(true)}
            >
              {t("campaignFlow.common.scheduleSendUpper")}
            </button>
            <button
              className="button button--primary"
              onClick={async () => {
                await handleSendCampaign(); // send instantly
              }}
              disabled={isSending}
            >
              {isSending ? t("campaignFlow.common.sending") : t("campaignFlow.common.sendNowUpper")}
            </button>
          </div>

          {popupType && <EmailStatusPopup type={popupType} onClose={() => setPopupType(null)} />}

          {showSchedulePopup && (
            <div className="schedule-modal-overlay">
              <div className="schedule-modal">
                <h4 className="schedule-picker-title">{t("campaignFlow.common.scheduleCampaign")}</h4>
                <p className="schedule-picker-paragraph">{t("campaignFlow.common.pakistanStandardTime")}</p>

                <div className="schedule-picker-presets">
                  {[
                    {
                      label: `${t("campaignFlow.common.tomorrowMorning")} 9:00 AM`,
                      time: "09:00",
                      kind: "tomorrowMorning" as const,
                    },
                    {
                      label: `${t("campaignFlow.common.tomorrowEvening")} 6:00 PM`,
                      time: "18:00",
                      kind: "tomorrowEvening" as const,
                    },
                    {
                      label: `${t("campaignFlow.common.nextMondayMorning")} 9:00 AM`,
                      time: "09:00",
                      dayOffset: (8 - new Date().getDay()) % 7 || 7,
                      kind: "nextMonday" as const,
                    },
                  ].map((preset, index) => {
                    const { dateStr, time } = getPresetDateTime(preset);
                    const isSelected =
                      scheduledDate === dateStr && scheduledTime === time;
                    return (
                      <button
                        key={index}
                        className={`preset-button ${
                          isSelected ? "preset-button--active" : ""
                        }`}
                        onClick={() => handleQuickSchedule(preset)}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>

                <label className="schedule-label">{t("campaignFlow.common.selectDate")}</label>
                <input
                  type="date"
                  className="schedule-input"
                  min={new Date().toISOString().split("T")[0]}
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)}
                />

                <label className="schedule-label">{t("campaignFlow.common.selectTime")}</label>
                <input
                  type="time"
                  className="schedule-input"
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e.target.value)}
                />

                <div className="schedule-actions">
                  <button
                    className="button button--primary proceed-button"
                    onClick={async () => {
                      if (!scheduledDate || !scheduledTime) {return;}
                      const scheduleDateTime = new Date(
                        `${scheduledDate}T${scheduledTime}:00Z`
                      ).toISOString();

                      await handleSendCampaign(scheduleDateTime);
                      setShowSchedulePopup(false); // close the modal after success
                    }}
                    disabled={!scheduledDate || !scheduledTime || isSending}
                  >
                    {isSending ? t("campaignFlow.common.scheduling") : t("campaignFlow.common.schedule")}
                  </button>

                  <button
                    className="button button--primary proceed-button"
                    onClick={() => setShowSchedulePopup(false)}
                  >
                    {t("campaignFlow.common.cancel")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewAndSend;
